import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TakeAwayStoreToken } from '@sleed/bizcommerce-api';
import { takeAwayStoresApi, binaryRepoImagesApi } from '~/lib/api/client';
import { formToToken, type TakeawayStoreFormValues } from '~/lib/schemas/takeaway-store';

export const takeawayStoreKeys = {
	all: ['takeaway-stores'] as const,
	list: () => [...takeawayStoreKeys.all, 'list'] as const,
	detail: (id: number | string) => [...takeawayStoreKeys.all, 'detail', id] as const
};

/** All pickup points (`GetAll` needs the Orders or OrderApproval role — pass `enabled: false` to skip it when denied). */
export function useTakeawayStoresList({ enabled = true }: { enabled?: boolean } = {}) {
	return useQuery<TakeAwayStoreToken[]>({
		queryKey: takeawayStoreKeys.list(),
		queryFn: async () => (await takeAwayStoresApi.getAll()).data.items ?? [],
		enabled
	});
}

/** Single store by id (full record incl. address/hours/description/images). `create` → null. */
export function useTakeawayStore(id: string) {
	const numericId = Number(id);
	const enabled = Number.isFinite(numericId) && numericId > 0;
	return useQuery<TakeAwayStoreToken | null>({
		queryKey: takeawayStoreKeys.detail(id),
		queryFn: async () => (enabled ? (await takeAwayStoresApi.getById(numericId)).data : null),
		enabled
	});
}

/**
 * Upload a store image into slot `key` ("1".."4"). The store must already exist (id > 0); the File is
 * attached as form-data keyed by the slot subkey (mirrors the manufacturer upload + the legacy
 * BinaryRepoImages/UploadTakeAwayStore contract).
 */
export async function uploadTakeawayStoreImage(storeId: number, key: '1' | '2' | '3' | '4', file: File): Promise<void> {
	const fd = new FormData();
	fd.append(key, file);
	await binaryRepoImagesApi.uploadTakeAwayStore(storeId, key, { data: fd });
}

export function useTakeawayStoreMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: takeawayStoreKeys.all });
	const create = useMutation({
		mutationFn: async (form: TakeawayStoreFormValues) => (await takeAwayStoresApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: TakeawayStoreFormValues) => (await takeAwayStoresApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await takeAwayStoresApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
